import { useMemo } from 'react';

import ActivitySheet from '../shared/sheet/activity-sheet';
import SubActivityView from '../sub-activity/sub-activity-view';
import { ViewProps } from './table.constants';
import { ClassroomAnswerInstruction } from 'approot/activity-builder/classroom/classroom-answer.style';
import ClassroomAnswerTabs, {
  INDIVIDUAL_VIEW,
  QUESTION_VIEW,
  SUGGESTED_VIEW,
} from 'approot/activity-builder/classroom/classroom-answer-tabs';
import { SUGGESTED_ANSWER_VARY } from 'global/constants';
import { useTeacherGetAnswers } from 'approot/activity-builder/present/activity-answers.hooks';
import { useActivityBuilderFlags } from 'approot/activity-builder/activity-builder-flags';
import { useBatchAnswersContext } from '../shared/batch-answers.context';
import useInitialClassroomTab from 'approot/activity-builder/classroom/use-initial-classroom-tab';
import TableViewFields from './table-view-fields';
import { useFindComponentDimensions } from '../component.hooks';
import { COMPONENT_SELECTOR } from '../shared/logic/get-component-selector-id';
import TableResponseIndividual from 'approot/activity-builder/classroom/table/table-response-individual';
import TableResponseSuggestedAnswers from 'approot/activity-builder/classroom/table/table-response-suggested-answers';
import { TableWrapper } from './table-check-answer.style';
import {
  ActivitySheetGridMax,
  ActivitySheetInnerContent,
  ActivitySheetInnerMax,
} from '../shared/sheet/activity-sheet.style';
import ActivitySheetSaveStatus from '../shared/sheet/activity-sheet-save-status';

const TableCheckAnswer = (
  props: ViewProps & {
    isPublished: boolean;
    onClose: () => void;
  }
) => {
  const {
    isPublished,
    id,
    type,
    subActivityProperties,
    suggestedAnswerProperties,
    properties: {
      table: { isReadOnly },
    },
    onClose,
  } = props;
  const { activityBuilderRespondFlag } = useActivityBuilderFlags();
  const { width } = useFindComponentDimensions(props.id, COMPONENT_SELECTOR);

  const { tab, setTab } = useInitialClassroomTab({
    isVisible: true,
    canRespond: isPublished && activityBuilderRespondFlag,
    componentId: id,
  });

  const { componentAnswers, variables } = useTeacherGetAnswers({
    fetchPolicy: 'cache-only',
    componentId: id,
    skip: !activityBuilderRespondFlag,
  });

  const { handleUpdate } = useBatchAnswersContext();

  const showSuggestedAnswers = useMemo(
    () =>
      !!(
        suggestedAnswerProperties?.suggestedAnswerType ===
          SUGGESTED_ANSWER_VARY ||
        (suggestedAnswerProperties?.suggestedAnswers &&
          suggestedAnswerProperties.suggestedAnswers.length > 0)
      ),
    [suggestedAnswerProperties]
  );

  return (
    <ActivitySheet
      isVisible
      title="Table response"
      hasBackgroundColor={tab === QUESTION_VIEW}
      customActions={
        <ActivitySheetSaveStatus
          title="Table response"
          showStatus={tab === QUESTION_VIEW}
          onClose={() => {
            setTab(INDIVIDUAL_VIEW);
            onClose();
          }}
        />
      }
      tabsContent={
        !isReadOnly && (
          <ClassroomAnswerTabs
            isPublished={isPublished}
            includeIndividualAnswers
            includeSuggestedAnswers={showSuggestedAnswers}
            includeClassResponse={false}
            tab={tab}
            onSelect={option => setTab(option)}
            componentId={id}
            onCloseActivitySheet={() => {
              setTab(INDIVIDUAL_VIEW);
              onClose();
            }}
          />
        )
      }
    >
      <ActivitySheetInnerContent id="content">
        {tab === QUESTION_VIEW && (
          <ActivitySheetInnerMax>
            <ClassroomAnswerInstruction>
              <SubActivityView
                withTTSButton
                componentId={id}
                componentType={type}
                subActivityDetails={subActivityProperties}
              >
                <TableWrapper $maxWidth={width}>
                  <TableViewFields
                    {...props}
                    canRespond={activityBuilderRespondFlag}
                    answers={componentAnswers}
                    updateAnswer={tableAnswer => {
                      if (
                        variables &&
                        subActivityProperties?.activityComponentId
                      ) {
                        handleUpdate({
                          input: {
                            __typename: 'ActivityAnswer',
                            activityId:
                              subActivityProperties?.activityComponentId,
                            answerType: tableAnswer.answerType,
                            componentId: id,
                            uuid: tableAnswer.uuid,
                            answer: tableAnswer.answer,
                            answerReferences: tableAnswer.answerReferences,
                            createdAt: new Date(),
                            updatedAt: null,
                            studentId: null,
                          },
                          variables,
                        });
                      }
                    }}
                  />
                </TableWrapper>
              </SubActivityView>
            </ClassroomAnswerInstruction>
          </ActivitySheetInnerMax>
        )}
        {tab === INDIVIDUAL_VIEW && (
          <ActivitySheetGridMax>
            <TableResponseIndividual component={props} width={width} />
          </ActivitySheetGridMax>
        )}
        {tab === SUGGESTED_VIEW && (
          <ActivitySheetInnerMax>
            <TableResponseSuggestedAnswers component={props} width={width} />
          </ActivitySheetInnerMax>
        )}
      </ActivitySheetInnerContent>
    </ActivitySheet>
  );
};

export default TableCheckAnswer;
